import type { AttachmentList } from "@esi/shared";
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useSession } from "../../session.tsx";
import { listAttachments } from "./api.ts";
import { accessDenied, attachmentDraft, attachmentPollMs } from "./controller.ts";
import { attachmentSessionGeneration } from "./session.ts";

export const attachmentKeys = {
  all: ["conversation-attachments"] as const,
  list: (id: string) => ["conversation-attachments", id] as const,
};
const accessVersions = new WeakMap<QueryClient, Map<string, number>>();
/** Bumped by each attachment refusal: a read begun under an older version must not write. */
export const accessVersion = (client: QueryClient, id: string) =>
  accessVersions.get(client)?.get(id) ?? 0;

/** Access loss replaces cached metadata and passage text, rather than leaving stale titles. */
export function clearAttachmentAccess(client: QueryClient, id: string) {
  let versions = accessVersions.get(client);
  if (!versions) {
    versions = new Map();
    accessVersions.set(client, versions);
  }
  versions.set(id, accessVersion(client, id) + 1);
  // Fence both observed and manual reads that began before this refusal.
  void client.cancelQueries({ queryKey: attachmentKeys.list(id) });
  client.setQueryData(attachmentKeys.list(id), { attachments: [] });
  client.setQueriesData({ queryKey: ["citation-passage", id] }, () => null);
}
export function invalidateAttachments(client: QueryClient, id: string) {
  void client.invalidateQueries({ queryKey: attachmentKeys.list(id) });
  void client.invalidateQueries({ queryKey: ["conversation", id] });
  void client.invalidateQueries({ queryKey: ["citation-passage", id] });
}
export async function freshAttachmentList(client: QueryClient, id: string, signal?: AbortSignal) {
  const generation = attachmentSessionGeneration();
  const version = accessVersion(client, id);
  try {
    const list = await listAttachments(id, signal);
    if (
      signal?.aborted ||
      generation !== attachmentSessionGeneration() ||
      version !== accessVersion(client, id)
    ) {
      throw new Error("session_changed");
    }
    client.setQueryData(attachmentKeys.list(id), list);
    return list;
  } catch (error) {
    if (
      generation === attachmentSessionGeneration() &&
      version === accessVersion(client, id) &&
      !signal?.aborted &&
      accessDenied(error)
    ) {
      clearAttachmentAccess(client, id);
    }
    throw error;
  }
}
export function useAttachments(conversationId: string) {
  const client = useQueryClient();
  const { csrfToken } = useSession();
  const draft = attachmentDraft(conversationId, csrfToken);
  // Only whether a browser upload is in flight: typing in the composer never re-renders here.
  const uploading = useSyncExternalStore(draft.subscribe, () =>
    draft.snapshot().files.some((f) => f.phase === "uploading" || f.phase === "queued"),
  );
  const verified = useRef<string | undefined>(undefined);
  const accessKey = `${conversationId}:${attachmentSessionGeneration()}`;
  // A refused list (403, 404) is not read again on focus or by the poll until the access version
  // moves (another refusal) or the next visit, which starts from a live check (#208).
  const refused = useRef<string | undefined>(undefined);
  const refusalKey = `${accessKey}:${accessVersion(client, conversationId)}`;
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const visibility = () => {
      const shown = document.visibilityState !== "hidden";
      setVisible(shown);
    };
    const revalidate = () => {
      // Query Core handles visibilitychange, but does not listen to native window focus.
      void client.invalidateQueries(
        { queryKey: attachmentKeys.list(conversationId) },
        { cancelRefetch: false },
      );
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("focus", revalidate);
    };
  }, [client, conversationId]);
  const query = useQuery({
    queryKey: attachmentKeys.list(conversationId),
    queryFn: async ({ signal }) => {
      try {
        const list = await freshAttachmentList(client, conversationId, signal);
        verified.current = accessKey;
        return list;
      } catch (error) {
        if (accessDenied(error)) {
          refused.current = `${accessKey}:${accessVersion(client, conversationId)}`;
        }
        throw error;
      }
    },
    enabled: refused.current !== refusalKey,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: (q) =>
      visible && uploading ? 2_000 : attachmentPollMs(q.state.data, visible),
    refetchIntervalInBackground: false,
  });
  // Each visit starts from a live access check. Never flash the previous viewer's title.
  const data: AttachmentList | undefined =
    query.status !== "pending" && query.isFetchedAfterMount && verified.current === accessKey
      ? query.data
      : undefined;
  // A failure other than an access refusal (refusals clear the list instead), shown only while
  // nothing authorized is: never stale names beside it.
  const error =
    data === undefined && query.isError && query.isFetchedAfterMount && !accessDenied(query.error)
      ? query.error
      : undefined;
  const { refetch } = query;
  const retry = useCallback(() => void refetch(), [refetch]);
  return { data, error, refetch: retry };
}
/** The live draft of a conversation, configured for this session, without subscribing to it. */
export function useConfiguredDraft(conversationId: string) {
  const { csrfToken } = useSession();
  const client = useQueryClient();
  const draft = attachmentDraft(conversationId, csrfToken);
  useEffect(() => {
    draft.configure(csrfToken, () => invalidateAttachments(client, conversationId));
  }, [draft, csrfToken, client, conversationId]);
  return draft;
}
export function useAttachmentDraft(conversationId: string) {
  const draft = useConfiguredDraft(conversationId);
  const state = useSyncExternalStore(draft.subscribe, draft.snapshot);
  return { draft, state };
}
